import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Button,
	Input,
	useToast,
	VStack,
	Text,
	FormControl,
	FormLabel,
	InputGroup,
	InputRightElement,
	Select,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import globalStore from '../store';
import { CalendarIcon } from '@chakra-ui/icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Countries } from '../remote_config/enums';
import { Platform } from '@repo/alictus-common/enums/platform';
import config from '../config';

const ENDPOINT = `${config.API_ENDPOINT}/game/com.ck.tripletiles/segment/fetch_by_playfabid`;
const USERS_ENDPOINT = `${config.API_ENDPOINT}/game/com.ck.tripletiles/segment/users-devices`;
const OVERRIDE_AUTH_TOKEN = '5hmSZzNtiZTOcKdRtLSPPqHxWbiKZx35';

// Prepare country list for dropdown
const countryList = Countries.filter((country) => country.code !== 'All').map((country) => ({
	value: country.code,
	label: country.name,
}));

type Device = {
	id: string;
	label: string;
};

type User = {
	id: string;
	name: string;
	devices: Device[];
};

export default function FetchByPlayFabModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
	const [playFabId, setPlayFabId] = useState('');
	const [loading, setLoading] = useState(false);
	const [segments, setSegments] = useState<string[]>([]);
	const [country, setCountry] = useState<string | null>(null);
	const [manualInput, setManualInput] = useState('');
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [version, setVersion] = useState('');
	const [buildNumber, setBuildNumber] = useState('');
	const [platform, setPlatform] = useState(Platform.iOS); // default to iOS
	const [newUser, setNewUser] = useState(false); // default false
	//const [isAlictusUser, setIsAlictusUser] = useState(false); // default false
	const [selectedUserId, setSelectedUserId] = useState<string>('');
	const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
	const [users, setUsers] = useState<User[]>([]);
	const [loadingUsers, setLoadingUsers] = useState(false);

	const sortedUsers = users.sort((a, b) => a.name.localeCompare(b.name));
	const selectedUser = users.find((u) => String(u.id) === selectedUserId);

	const fetchUsersAndDevices = async () => {
		setLoadingUsers(true);
		try {
			const headers = {
				'Content-Type': 'application/json',
				Authorization: 'Bearer ' + globalData.JWTToken,
			};

			const response = await axios.get(USERS_ENDPOINT, { headers });
			setUsers(response.data?.users || []);
		} catch (error) {
			console.error('Error fetching users and devices:', error);
			toast({
				title: 'Error fetching users and devices',
				description: 'Failed to load users and devices',
				status: 'error',
				duration: 4000,
				isClosable: true,
				position: 'top',
			});
		} finally {
			setLoadingUsers(false);
		}
	};

	useEffect(() => {
		if (!isOpen) {
			setPlayFabId('');
			setLoading(false);
			setSegments([]);
			setCountry(null);
			setManualInput('');
			setSelectedDate(null);
			setVersion('');
			setBuildNumber('');
			setPlatform(Platform.iOS);
			setNewUser(false);
			setSelectedUserId('');
			setSelectedDeviceId('');
			setUsers([]);
			setLoadingUsers(false);
		}
		if (isOpen) {
			fetchUsersAndDevices();
		}
	}, [isOpen]);

	const toast = useToast();
	let globalData = globalStore();

	// Handle time input (epoch seconds)
	const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setManualInput(value);
		if (!isNaN(Number(value)) && Number(value) > 0) {
			setSelectedDate(new Date(Number(value) * 1000));
		} else {
			setSelectedDate(null);
		}
	};

	const handleDateChange = (date: Date | null) => {
		setSelectedDate(date);
		if (date) {
			const epochTime = Math.floor(date.getTime() / 1000);
			setManualInput(epochTime.toString());
		} else {
			setManualInput('');
		}
	};

const handleFetch = async () => {
    setLoading(true);
    setSegments([]);
    try {
        const body = {
            PlayerId: '',
            RcIds: [],
            ABTests: [],
            PlayfabId: playFabId,
            BundleId: 'com.ck.tripletiles',
            Platform: platform,
            DeviceId: selectedDeviceId || '',
            Version: version ? version : '',
            BuildNumber: buildNumber ? Number(buildNumber) : null,
            NewUser: newUser,
            BackendTimestamp: 1000,
        };

        const headers: any = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + globalData.JWTToken,
        };

        // Add override headers only if needed
        let overrideUsed = false;
        if (country) {
            headers['X-Country-Override'] = country;
            overrideUsed = true;
        }
        if (manualInput && !isNaN(Number(manualInput)) && Number(manualInput) > 0) {
            headers['X-Time-Override'] = manualInput;
            overrideUsed = true;
        }
        if (overrideUsed) {
            headers['X-Override-Auth'] = OVERRIDE_AUTH_TOKEN;
        }

        console.log('Fetching segments with body:', body, 'and headers:', headers);
        const res = await axios.post(ENDPOINT, body, { headers });
        console.log('Fetch segments response:', res);

        setSegments(res.data?.Segments || []);

        if (!res.data?.Segments?.length) {
            toast({
                title: 'No segments found',
                status: 'info',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        }
    } catch (e: any) {
        console.error('Error fetching segments:', e);
        
        // Safely extract error message from different possible error structures
        let errorMessage = 'Unknown error occurred';
        let errorDetails = '';

        if (e.response) {
            // Server responded with an error status
            if (e.response.data) {
                if (typeof e.response.data === 'string') {
                    errorMessage = e.response.data;
                } else if (e.response.data.error) {
                    errorMessage = e.response.data.error;
                    if (e.response.data.details) {
                        errorDetails = e.response.data.details;
                    }
                } else {
                    errorMessage = JSON.stringify(e.response.data);
                }
            } else {
                errorMessage = `Server error: ${e.response.status} ${e.response.statusText}`;
            }
        } else if (e.request) {
            // Request was made but no response received
            errorMessage = 'No response from server. Please check your connection.';
        } else if (e.message) {
            // Something else happened
            errorMessage = e.message;
        }

        // Show appropriate toast based on error type
        const isValidationError = e.response?.status === 400;
        
        toast({
            title: isValidationError ? 'Validation Error' : 'Error fetching segments',
            description: errorDetails ? `${errorMessage}\n\nDetails: ${errorDetails}` : errorMessage,
            status: isValidationError ? 'warning' : 'error',
            duration: isValidationError ? 8000 : 6000,
            isClosable: true,
            position: 'top',
        });
    } finally {
        setLoading(false);
    }
};

	return (
		<Modal isOpen={isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Fetch Segments by PlayFabId</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<FormControl mb={4} isRequired>
						<FormLabel>PlayFabId</FormLabel>
						<Input placeholder="Enter PlayFabId" value={playFabId} onChange={(e) => setPlayFabId(e.target.value)} />
					</FormControl>
					<FormControl mb={4} isRequired>
						<FormLabel>Platform</FormLabel>
						<Select value={platform} onChange={(e) => setPlatform(Number(e.target.value))}>
							<option value={Platform.iOS}>iOS</option>
							<option value={Platform.Android}>Android</option>
						</Select>
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>Version (optional)</FormLabel>
						<Input placeholder="e.g. 1.20.0" value={version} onChange={(e) => setVersion(e.target.value)} />
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>Build Number (optional)</FormLabel>
						<Input placeholder="e.g. 3094" type="number" value={buildNumber} onChange={(e) => setBuildNumber(e.target.value)} />
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>Country (optional)</FormLabel>
						<Select placeholder="Select Country" value={country || ''} onChange={(e) => setCountry(e.target.value || null)}>
							<option value="">(none)</option>
							{countryList.map((c) => (
								<option key={c.value} value={c.value}>
									{c.label}
								</option>
							))}
						</Select>
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>Time (Epoch, optional)</FormLabel>
						<InputGroup>
							<Input type="number" step="1" value={manualInput} onChange={handleManualInputChange} placeholder="Enter epoch time" />
							<InputRightElement opacity={0.5} pointerEvents="none" minW={180}>
								{manualInput && !isNaN(Number(manualInput)) && Number(manualInput) > 0
									? new Date(Number(manualInput) * 1000).toLocaleString('tr-TR')
									: ''}
							</InputRightElement>
						</InputGroup>
						<DatePicker
							selected={selectedDate}
							onChange={handleDateChange}
							showTimeSelect
							timeIntervals={1}
							customInput={
								<Button size="sm" mt="1" colorScheme="blue" leftIcon={<CalendarIcon />}>
									Pick
								</Button>
							}
						/>
					</FormControl>
					{/* 					<FormControl mb={4}>
						<FormLabel>Is Alictus User</FormLabel>
						<Select value={String(isAlictusUser)} onChange={(e) => setIsAlictusUser(e.target.value === 'true')}>
							<option value="true">Yes</option>
							<option value="false">No</option>
						</Select>
					</FormControl> */}
					<FormControl mb={4}>
						<FormLabel>User (optional)</FormLabel>
						<Select
							placeholder={loadingUsers ? 'Loading users...' : 'Select User'}
							value={selectedUserId}
							onChange={(e) => {
								setSelectedUserId(e.target.value);
								setSelectedDeviceId(''); // Reset device when user changes
							}}
							isDisabled={loadingUsers}
						>
							{sortedUsers.map((user) => (
								<option key={user.id} value={user.id}>
									{user.name}
								</option>
							))}
						</Select>
					</FormControl>
					{/* Device Dropdown */}
					<FormControl mb={4}>
						<FormLabel>Device ID (optional)</FormLabel>
						<Select
							placeholder="Select Device"
							value={selectedDeviceId}
							onChange={(e) => setSelectedDeviceId(e.target.value)}
							isDisabled={!selectedUser || loadingUsers}
						>
							{selectedUser?.devices.map((device) => (
								<option key={device.id} value={device.id}>
									{device.label} ({device.id})
								</option>
							))}
						</Select>
					</FormControl>
					<FormControl mb={4}>
						<FormLabel>New User (optional)</FormLabel>
						<Select value={String(newUser)} onChange={(e) => setNewUser(e.target.value === 'true')}>
							<option value="false">No</option>
							<option value="true">Yes</option>
						</Select>
					</FormControl>
					<Button colorScheme="blue" onClick={handleFetch} isLoading={loading} isDisabled={!playFabId} mb={3} width="100%">
						Fetch
					</Button>
					<VStack align="start" spacing={1}>
						{segments.length > 0 && <Text fontWeight="bold">Segments:</Text>}
						{segments.map((seg, idx) => (
							<Text key={idx} fontSize="sm" pl={2}>
								• {seg}
							</Text>
						))}
					</VStack>
				</ModalBody>
				<ModalFooter>
					<Button onClick={onClose}>Close</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
